import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import { updateWorkOrdersInput } from '@atom/actions/workOrdersInputActions';
import HistoryDrawer from '@atom/components/common/history/HistoryDrawer';
import PriorityControl from '@atom/components/common/PriorityControl';
import WorkOrderDetailEdit from '@atom/components/common/workOrderDetail/workOrderDetailEdit/WorkOrderDetailEdit';
import WorkOrderStatusEdit from '@atom/components/common/workOrderDetail/workOrderStatusEdit/WorkOrderStatusEdit';
import { GET_TIME_ENTRIES_STATUS } from '@atom/graph/timeEntry';
import {
  WORK_ORDER_DELETE,
  WORK_ORDER_DUPLICATE,
  WORK_ORDER_UPDATE,
} from '@atom/graph/work';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import {
  Button,
  Icon,
  IconButton,
  Menu,
  Modal,
  Progress,
  Snackbar,
} from '@atom/mui';
import {
  ArrowBackIcon,
  ContentCopyIcon,
  DeleteIcon,
  DescriptionIcon,
  InfoIcon,
  InsertPhotoIcon,
  PlaylistAddCheckIcon,
  SupervisorAccountIcon,
  VideoLibraryIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { RootState } from '@atom/types/store';
import { TimeEntryStatus } from '@atom/types/timeEntry';
import { WorkOrderDetailType, WorkOrderTab } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  isManagerWorkDeleteRoleEnabled,
  isOwnWorkVersioningEnabled,
} from '@atom/utilities/tenantFeatureUtilities';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';
import { CLOSED_STATUS_ID } from '@atom/utilities/workOrderStatusUtilities';

import WorkOrderContext, { WorkOrderActionTypes } from '../WorkOrderContext';

import DetailHeaderButton from './DetailHeaderButton';
import HeaderInfoSection from './HeaderInfoSection';
import ReportModal from './ReportModal';
import { getReportOptions } from './reportOptions';
import WorkOrderVersionButton from './WorkOrderVersionButton';

import classes from './workOrderDetailHeader.module.css';

const { MenuItem } = Menu;

const styles = {
  icon: {
    marginRight: '0.5rem',
  },
  name: {
    width: '100%',
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  removeButton: {
    background: colors.brand.red,
    color: colors.neutral.white,
  },
};

const views = [
  {
    value: WorkOrderTab.INFO,
    label: 'Info',
    icon: InfoIcon,
    dataCy: 'workInfoTab',
  },
  {
    value: WorkOrderTab.TASKS,
    label: 'Tasks',
    icon: PlaylistAddCheckIcon,
    dataCy: 'workTasksTab',
  },
  {
    value: WorkOrderTab.TEAM,
    label: 'Team',
    icon: SupervisorAccountIcon,
    dataCy: 'workTeamTab',
  },
  {
    value: WorkOrderTab.DOCUMENTS,
    label: 'Documents',
    icon: DescriptionIcon,
    dataCy: 'workDocumentsTab',
  },
  {
    value: WorkOrderTab.PHOTOS,
    label: 'Photos',
    icon: InsertPhotoIcon,
    dataCy: 'workPhotosTab',
  },
  {
    value: WorkOrderTab.VIDEOS,
    label: 'Videos',
    icon: VideoLibraryIcon,
    dataCy: 'workVideosTab',
  },
];

export interface Props {
  activeView: WorkOrderTab;
  setActiveView: (view: WorkOrderTab) => void;
}

const DetailHeader = ({ activeView, setActiveView }: Props) => {
  const container = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const userProfile = useUserProfile();
  const preferences = usePreferences();
  const deleteOwnWork = preferences?.workOrders?.deleteOwnWork;
  const reportTypes = preferences?.reports || [];

  const { workValidations } = useWorkValidations();

  const [, { backDestination, navigate }] = useNavigation();

  const input = useSelector(
    (rootState: RootState) => rootState.workOrdersInput,
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);

  const {
    workOrderDetail,
    loading,
    dispatch: contextDispatch,
    refetch,
    setActiveWorkOrderAsset,
    activeWorkOrderAsset,
  } = useContext(WorkOrderContext);

  const [duplicateWorkOrder, { loading: loadingDuplicate }] =
    useMutation(WORK_ORDER_DUPLICATE);

  const [deleteWorkOrder, { loading: loadingDelete }] =
    useMutation(WORK_ORDER_DELETE);

  const [
    getTimeEntriesStatus,
    { loading: timeEntriesStatusLoading, data: timeEntriesStatusData },
  ] = useLazyQuery(GET_TIME_ENTRIES_STATUS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (deleteModalOpen || duplicateModalOpen) {
      getTimeEntriesStatus({
        variables: {
          input: {
            workOrderId: workOrderDetail.id,
            statuses: [TimeEntryStatus.approved],
          },
        },
      });
    }
  }, [deleteModalOpen, duplicateModalOpen]);

  const [updateWorkDetail] = useMutation(WORK_ORDER_UPDATE);

  const updateWorkOrderProperty = (
    property: keyof WorkOrderDetailType,
    value: any,
  ) => {
    contextDispatch({
      type: WorkOrderActionTypes.UPDATE_WORK_ORDER_PROPERTY,
      data: {
        property,
        value,
      },
    });
  };

  const updateWorkOrder = async params => {
    const { priorityId } = params;

    if (R.isNil(priorityId)) {
      return;
    }

    const res = await updateWorkDetail({
      variables: {
        workOrder: {
          id: workOrderDetail.id,
          priorityId,
        },
      },
    });

    updateWorkOrderProperty(
      'priorityId',
      res?.data?.workOrderUpdate.priorityId,
    );
  };

  const navigateBackToWorkOrders = () => {
    if (activeWorkOrderAsset) {
      setActiveWorkOrderAsset('');
    } else {
      navigate(backDestination('/workOrders'));
    }
  };

  const handleDeleteWorkOrder = async () => {
    try {
      await deleteWorkOrder({
        variables: {
          id: workOrderDetail.id,
        },
      });

      dispatch(updateWorkOrdersInput({ ...input, workOrderId: undefined }));
      navigate('/workOrders');

      Snackbar.info({
        message: 'Work deleted',
      });
    } catch (error) {
      const message =
        getErrorCode(error) === 423
          ? 'Cannot delete a work used to create a work template.'
          : 'An error occurred. Please try again.';

      Snackbar.error({ message });

      setDeleteModalOpen(false);
    }
  };

  const handleDuplicate = async () => {
    const { id, name } = workOrderDetail;

    Snackbar.info({ message: `Duplicating work ${name}...` });

    try {
      const { data } = await duplicateWorkOrder({ variables: { id } });

      Snackbar.info({
        message: `Duplicated work ${name}.`,
        action: 'View',
        onActionClick: () => navigate(`/workOrders/${data.workOrderDuplicate}`),
      });
    } catch (error) {
      Snackbar.error({ message: `Failed to duplicate work ${name}.` });
    }

    setDuplicateModalOpen(false);
  };

  const hasApprovedTimeEntries =
    timeEntriesStatusData?.timeEntries?.totalCount > 0;

  const getDeleteModalValue = () => {
    return hasApprovedTimeEntries
      ? {
          title: `Cannot Delete Work`,
          content: 'Work with approved work time cannot be deleted.',
        }
      : {
          title: 'Delete Work',
          content: 'Are you sure you want to delete this work?',
        };
  };

  const getDuplicateModalValue = () => {
    return {
      title: `Duplicate Work`,
      content: 'Are you sure you want to duplicate this work?',
    };
  };

  const deleteModalLoading = loadingDelete || timeEntriesStatusLoading;
  const duplicateModalLoading = loadingDuplicate || timeEntriesStatusLoading;

  const getDeleteFooter = () => {
    return hasApprovedTimeEntries ? (
      <Button onClick={() => setDeleteModalOpen(false)}>OK</Button>
    ) : (
      <div>
        <Button
          onClick={() => setDeleteModalOpen(false)}
          style={{ marginRight: '0.5rem' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={deleteModalLoading}
          onClick={handleDeleteWorkOrder}
          style={styles.removeButton}
        >
          Delete
        </Button>
      </div>
    );
  };

  const getDuplicateFooter = () => {
    return (
      <div>
        <Button
          onClick={() => setDuplicateModalOpen(false)}
          style={{ marginRight: '0.5rem' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={duplicateModalLoading}
          onClick={handleDuplicate}
          color="primary"
        >
          Duplicate
        </Button>
      </div>
    );
  };

  const hasAsset = !R.isEmpty(workOrderDetail.inventoryAssetId);

  const canDeleteOwnWork =
    deleteOwnWork &&
    userProfile.userId === workOrderDetail.createdBy &&
    workOrderDetail?.statusId !== CLOSED_STATUS_ID &&
    !workOrderDetail?.reopened;

  const isDeleteEnabled = canDeleteOwnWork
    ? hasRolePermissions(ROLE_SETS.INSPECTOR)
    : isManagerWorkDeleteRoleEnabled
      ? hasRolePermissions(ROLE_SETS.MANAGER)
      : hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  const isDuplicateEnabled = hasRolePermissions(ROLE_SETS.INSPECTOR);
  const showActionMenu = isDeleteEnabled || isDuplicateEnabled;

  const workNameWidth = `${container.current?.offsetWidth - 400}px`;

  const deleteModalValue = getDeleteModalValue();
  const duplicateModalValue = getDuplicateModalValue();

  const showReportModal =
    !isNilOrEmpty(reportTypes) &&
    (hasAsset ||
      getReportOptions(reportTypes).some(option => !option.requiresAsset));

  // User can version their own work if the current client is Hennepin
  // and work is created by logged in user.
  const canVersionOwnWork =
    isOwnWorkVersioningEnabled &&
    userProfile?.userId === workOrderDetail.createdBy;

  const showVersionButton =
    workOrderDetail.isClosed &&
    hasAsset &&
    (canVersionOwnWork
      ? hasRolePermissions(ROLE_SETS.INSPECTOR)
      : hasRolePermissions(ROLE_SETS.MANAGER));

  const hasError = view => {
    if (view.value === WorkOrderTab.INFO) {
      return !isNilOrEmpty(workValidations?.workValidations?.invalidWorkFields);
    }
    if (view.value === WorkOrderTab.TASKS) {
      return !R.isEmpty(workValidations?.taskValidations);
    }
    return false;
  };

  return (
    <div ref={container} className={classes.headerContainer}>
      <div>
        <IconButton onClick={navigateBackToWorkOrders} tooltip="Go back">
          <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
        </IconButton>
        <div style={{ width: workNameWidth }} className={classes.titleBar}>
          <span style={styles.name}>Work ID: {workOrderDetail.name}</span>
        </div>
      </div>
      <div className={classes.iconBar}>
        {loading && (
          <div className={classes.loadingContainer}>
            <Progress
              size={20}
              thickness={2}
              progressColor={colors.neutral.white}
            />
          </div>
        )}
        <WorkOrderStatusEdit
          workOrder={workOrderDetail}
          createdById={workOrderDetail?.createdBy}
          refetch={refetch}
          styleType="detail"
        />
        <PriorityControl
          disabled={workOrderDetail.isClosed}
          priorityId={workOrderDetail.priorityId}
          updateWorkOrder={updateWorkOrder}
        />
        {showReportModal && <ReportModal />}
        {showVersionButton && <WorkOrderVersionButton />}
        {!workOrderDetail.isClosed && (
          <WorkOrderDetailEdit
            workOrderDetail={workOrderDetail}
            refetch={refetch}
            iconColor={colors.neutral.white}
          />
        )}
        <HistoryDrawer subject="workOrders" id={workOrderDetail.id} />
        {showActionMenu && (
          <Menu
            IconProps={{ color: colors.neutral.white }}
            IconButtonProps={{ edge: 'end' }}
            dataCyLabel="workOrderDetailOptions"
          >
            {isDuplicateEnabled && (
              <MenuItem
                startAdornment={
                  <Icon style={styles.icon} icon={ContentCopyIcon} />
                }
                onClick={() => setDuplicateModalOpen(true)}
                disabled={loadingDuplicate}
                data-cy="workOrderDetailDuplicateWork"
              >
                Duplicate
              </MenuItem>
            )}
            {isDeleteEnabled && (
              <MenuItem
                onClick={() => setDeleteModalOpen(true)}
                startAdornment={<Icon style={styles.icon} icon={DeleteIcon} />}
                data-cy="workOrderDetailDeleteWork"
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        )}
      </div>
      <HeaderInfoSection />
      <div className={classes.iconRow}>
        {views.map(view => {
          return (
            <DetailHeaderButton
              key={view.value}
              view={view}
              activeView={activeView}
              setActiveView={setActiveView}
              bullet={hasError(view)}
            />
          );
        })}
      </div>
      <Modal
        open={deleteModalOpen}
        title={deleteModalLoading ? '' : deleteModalValue.title}
        footer={deleteModalLoading ? '' : getDeleteFooter()}
        data-cy="workOrderDeleteModal"
      >
        {deleteModalLoading ? (
          <div className={classes.loadingContainer}>
            <Progress />
          </div>
        ) : (
          deleteModalValue.content
        )}
      </Modal>
      <Modal
        open={duplicateModalOpen}
        title={duplicateModalLoading ? '' : duplicateModalValue.title}
        footer={duplicateModalLoading ? '' : getDuplicateFooter()}
        data-cy="workOrderDuplicateModal"
      >
        {duplicateModalLoading ? (
          <div className={classes.loadingContainer}>
            <Progress />
          </div>
        ) : (
          duplicateModalValue.content
        )}
      </Modal>
    </div>
  );
};

export default DetailHeader;
